import { Box, IconButton, Stack, SvgIcon } from '@mui/material';
import {
  useMeetingManager,
  useToggleLocalMute,
  useLocalVideo,
  useRosterState,
  useVideoInputs,
} from 'amazon-chime-sdk-component-library-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';

import ControlBarButton from './button';
import ControlBarSplitButton from './splitButton';

import InfoIcon from '../../../assets/svg/meeting/info.svg?react';
import NotesIcon from '../../../assets/svg/meeting/notes.svg?react';
import UsersIcon from '../../../assets/svg/meeting/users.svg?react';
import { ROLE_VALUES } from '../../../constants/customerConstant';
import { useAuth } from '../../../contexts/auth/authContext';
import {
  MeetingSidebar,
  MeetingSidebarType,
  useMeetingContext,
} from '../../../contexts/meeting/meetingContext';
import { useToastNotificationContext } from '../../../hooks/global/alert/useToastNotification';
import meetingService from '../../../services/meetingService';
import MicrophoneIcon from '../icons/microphoneIcon';
import PhoneIcon from '../icons/phoneIcon';
import VideoCameraIcon from '../icons/videoCameraIcon';

function EndCallButton() {
  const intl = useIntl();
  const queryParams = new URLSearchParams(location.search);
  const code = queryParams.get('code');
  // The URLSearchParams constructor interprets plus signs ( + ) as spaces, which might cause problems
  const fixedCode = code?.replace(' ', '+') ?? null;
  const { meetingID } = useParams<{ meetingID: string }>();
  const meetingManager = useMeetingManager();
  const { userType } = useAuth();
  const { setIsMeetingEnded, mediaPipelineID, meetingDetails } =
    useMeetingContext();
  const { roster: allRoster } = useRosterState();

  if (!meetingID) {
    throw new Error('Meeting ID is required');
  }

  if (userType === ROLE_VALUES.WHISTLE_BLOWER) {
    return (
      <Stack justifyContent='center' alignItems='center'>
        <Box marginBottom='4px'>
          <IconButton
            sx={{
              borderRadius: '76px',
              padding: '8px 16px',
              background: (theme) => theme.palette.red[500],
              '&:hover': {
                background: (theme) => theme.palette.red[400],
              },
            }}
            onClick={() => {
              meetingManager.leave();
            }}
          >
            <PhoneIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            color: (theme) => theme.palette.neutralBlackBg[50],
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: '500',
            lineHeight: '125%',
          }}
        >
          <FormattedMessage defaultMessage='Leave' />
        </Box>
      </Stack>
    );
  }

  return (
    <ControlBarSplitButton
      icon={<PhoneIcon />}
      label={intl.formatMessage({ defaultMessage: 'Leave' })}
      onLeaveClick={() => {
        meetingManager.leave();

        // If allRoster has 2 attendees, the remaining attendees are the current user and the media pipeline, so we can end the recording
        if (
          Object.keys(allRoster).length === 2 &&
          mediaPipelineID.current &&
          meetingDetails.current
        ) {
          meetingService.stopRecording(
            meetingDetails.current.Meeting.MeetingId,
            mediaPipelineID.current,
            {
              code: fixedCode,
              token: meetingID,
            },
          );
        }
      }}
      onEndClick={async () => {
        setIsMeetingEnded(true);
        meetingManager.leave();
        if (mediaPipelineID.current && meetingDetails.current) {
          await meetingService.stopRecording(
            meetingDetails.current.Meeting.MeetingId,
            mediaPipelineID.current,
            {
              code: fixedCode,
              token: meetingID,
            },
          );
        }
        meetingService.endMeeting(meetingID);
      }}
    />
  );
}

function ControlBar() {
  const intl = useIntl();
  const { muted, toggleMute } = useToggleLocalMute();
  const { isVideoEnabled, toggleVideo } = useLocalVideo();
  const { openSidebar, setOpenSidebar, isVoiceOnlyCall } = useMeetingContext();
  const { userType } = useAuth();
  const { createToastNotification } = useToastNotificationContext();
  const { selectedDevice } = useVideoInputs();
  const [queryParams] = useSearchParams();
  const code = queryParams.get('code');
  const name = queryParams.get('name');

  const isGuest = code && name;

  function toggleSidebar(sidebar: MeetingSidebarType) {
    setOpenSidebar((currentOpenSidebar) => {
      if (currentOpenSidebar && currentOpenSidebar === sidebar) {
        return null;
      }

      return sidebar;
    });
  }

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} paddingY={'.5rem'}>
      <Stack
        direction='row'
        justifyContent='flex-start'
        alignItems='center'
        flex='1 1 25%'
      ></Stack>
      <Stack
        direction='row'
        justifyContent='center'
        alignItems='center'
        spacing={3}
        flex='1 1 25%'
      >
        <ControlBarButton
          icon={<MicrophoneIcon muted={muted} />}
          label={
            muted
              ? intl.formatMessage({ defaultMessage: 'Unmute mic' })
              : intl.formatMessage({ defaultMessage: 'Mute mic' })
          }
          active={!muted}
          onClick={toggleMute}
        />
        {!isVoiceOnlyCall && (
          <ControlBarButton
            icon={<VideoCameraIcon videoEnabled={isVideoEnabled} />}
            label={
              isVideoEnabled
                ? intl.formatMessage({ defaultMessage: 'Video off' })
                : intl.formatMessage({ defaultMessage: 'Video on' })
            }
            active={isVideoEnabled}
            onClick={() => {
              toggleVideo();

              if (!selectedDevice) {
                createToastNotification(
                  intl.formatMessage({
                    defaultMessage: 'Please allow camera access',
                  }),
                  'error',
                );
              }
            }}
          />
        )}
        <EndCallButton />
      </Stack>
      <Stack
        direction='row'
        justifyContent={{ xs: 'center', md: 'flex-end' }}
        alignItems='center'
        spacing={3}
        flex='1 1 25%'
        marginRight={'1rem'}
      >
        <ControlBarButton
          icon={
            <SvgIcon component={InfoIcon} htmlColor='white' inheritViewBox />
          }
          label={intl.formatMessage({ defaultMessage: 'Info' })}
          onClick={() => toggleSidebar(MeetingSidebar.INFO)}
          active={openSidebar === MeetingSidebar.INFO}
        />
        <ControlBarButton
          icon={
            <SvgIcon component={UsersIcon} htmlColor='white' inheritViewBox />
          }
          label={intl.formatMessage({ defaultMessage: 'Attendees' })}
          onClick={() => toggleSidebar(MeetingSidebar.ATTENDEES)}
          active={openSidebar === MeetingSidebar.ATTENDEES}
        />
        {userType !== ROLE_VALUES.WHISTLE_BLOWER && !isGuest && (
          <ControlBarButton
            icon={
              <SvgIcon component={NotesIcon} htmlColor='white' inheritViewBox />
            }
            label={intl.formatMessage({ defaultMessage: 'Notes' })}
            onClick={() => toggleSidebar(MeetingSidebar.NOTES)}
            active={openSidebar === MeetingSidebar.NOTES}
          />
        )}
      </Stack>
    </Stack>
  );
}

export default ControlBar;
